import { useMutation, useQuery } from "@apollo/client/react";
import { DSButton } from "@components/design-system/molecules";
import { faArrowLeft } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import mixpanel from "mixpanel-browser";
import React, { useState, useEffect } from "react";

import ExtensionStep from "./PhoneSettingsModal/ExtensionStep";
import MessageDisplay from "./PhoneSettingsModal/MessageDisplay";
import OverviewStep from "./PhoneSettingsModal/OverviewStep";
import RequestStep from "./PhoneSettingsModal/RequestStep";
import VerifyStep from "./PhoneSettingsModal/VerifyStep";
import { detectCountry } from "./utils";
import {
  REQUEST_PHONE_VERIFICATION,
  DELETE_VERIFIED_PHONE,
} from "../../../graphql/mutations/PhoneVerification";
import { GET_DEFAULT_PHONE_NUMBER } from "../../../graphql/queries/DefaultPhoneNumber";
import { GET_USER_VERIFIED_PHONES } from "../../../graphql/queries/PhoneVerification";
import { getDigitsOnly } from "../../../utils/phoneUtils";
import { alert } from "../../common/Alert";

const PhoneVerificationStep = ({ onClose, onCallerIdChange }) => {
  // State
  const [userCountry, setUserCountry] = useState(null);
  const [currentStep, setCurrentStep] = useState("overview");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [extension, setExtension] = useState("");
  const [validationCode, setValidationCode] = useState("");
  const [selectedCallerId, setSelectedCallerId] = useState(
    () => localStorage.getItem("scribephoneCallerId") || "default",
  );

  // Message states
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [alreadyVerifiedMessage, setAlreadyVerifiedMessage] =
    useState("");
  const [deleteSuccessMessage, setDeleteSuccessMessage] =
    useState("");
  const [verificationProgress, setVerificationProgress] =
    useState("");

  // Queries and mutations
  const { data: defaultPhoneData } = useQuery(
    GET_DEFAULT_PHONE_NUMBER,
    {
      variables: { countryCode: userCountry },
      skip: !userCountry,
    },
  );
  const { refetch: refetchUserPhones } = useQuery(
    GET_USER_VERIFIED_PHONES,
  );
  const [requestVerification, { loading: requesting }] = useMutation(
    REQUEST_PHONE_VERIFICATION,
  );
  const [deletePhone, { loading: deleting }] = useMutation(
    DELETE_VERIFIED_PHONE,
  );
  const defaultPhoneNumber = defaultPhoneData?.defaultPhoneNumber;

  // Utility functions

  const clearMessages = () => {
    setError("");
    setSuccess("");
    setAlreadyVerifiedMessage("");
    setDeleteSuccessMessage("");
    setVerificationProgress("");
  };

  const navigateToStep = (step) => {
    clearMessages();
    setCurrentStep(step);
  };

  const handleCallerIdChange = (callerId) => {
    setSelectedCallerId(callerId);
    localStorage.setItem("scribephoneCallerId", callerId);
    if (onCallerIdChange) {
      onCallerIdChange(callerId);
    }
  };

  // Event handlers
  const handleDone = () => {
    clearMessages();
    setCurrentStep("overview");
    refetchUserPhones();
  };

  const handleRefresh = () => {
    refetchUserPhones();
    clearMessages();
  };

  const handleRequestVerification = async (teamUuid = null) => {
    if (!phoneNumber.trim()) {
      setError("Please enter a phone number");
      return;
    }

    clearMessages();

    try {
      const hasExtension =
        extension && Array.isArray(extension) && extension.length > 0;

      const variables = {
        phoneNumber: getDigitsOnly(phoneNumber),
        teamUuid: teamUuid,
        ...(hasExtension && { extension }),
      };

      if (hasExtension) {
        setVerificationProgress(
          "Initiating verification call with extension...",
        );
      }

      const result = await requestVerification({
        variables,
      });

      if (result.data?.requestPhoneVerification?.success) {
        const { validationCode, message } =
          result.data.requestPhoneVerification;
        setValidationCode(validationCode);

        if (!validationCode) {
          mixpanel.track("Phone Verification Success", {
            status: "Number already verified",
            teamUuid: teamUuid,
            timestamp: new Date().toISOString(),
          });

          setAlreadyVerifiedMessage(
            message ||
              "Phone number already verified and added to your list.",
          );
          setCurrentStep("overview");
        } else {
          mixpanel.track("Phone Verification Success", {
            status: "Verification initiated",
            teamUuid: teamUuid,
            timestamp: new Date().toISOString(),
          });

          if (hasExtension) {
            setVerificationProgress(
              "The call is making its way through the extension sequence. Please allow for extra time for the call to reach you. If you don't receive a call after 2 minutes, please try again or contact support.",
            );
          }

          setCurrentStep("verify");
        }
      } else {
        mixpanel.track("Phone Verification Failed", {
          error:
            result.data?.requestPhoneVerification?.message ||
            "Unknown error",
          teamUuid: teamUuid,
          timestamp: new Date().toISOString(),
        });

        const message =
          result.data?.requestPhoneVerification?.message ||
          "We couldn't verify your phone number. Please try again.";
        setError(message);
      }
    } catch (err) {
      mixpanel.track("Phone Verification Failed", {
        error: err.message || "Network error",
        teamUuid: teamUuid,
        timestamp: new Date().toISOString(),
      });

      console.error("Request verification error:", err);
      setError(
        "Something went wrong while verifying your phone number. Please try again.",
      );
    }
  };

  const handleDeletePhone = async (phoneUuid, phoneNumber) => {
    // Show confirmation dialog
    const isConfirmed = window.confirm(
      `Are you sure you want to delete the phone number ${phoneNumber}? This action cannot be undone.`,
    );

    if (!isConfirmed) {
      return; // User cancelled the deletion
    }

    try {
      const result = await deletePhone({
        variables: { verifiedPhoneUuid: phoneUuid },
      });

      if (result.data?.deleteVerifiedPhoneNumber?.success) {
        setDeleteSuccessMessage("Phone number removed successfully");

        // Reset caller ID if deleted number was selected
        if (selectedCallerId === phoneNumber) {
          handleCallerIdChange("default");
        }

        // Refetch the list to update the UI
        refetchUserPhones();
      } else {
        setError("We couldn't remove the phone number");
      }
    } catch (err) {
      console.error("Delete phone error:", err);
      setError(
        "We couldn't remove the phone number. Please try again.",
      );
    }
  };

  // Render helpers
  const renderOverviewStep = () => (
    <OverviewStep
      selectedCallerId={selectedCallerId}
      onSelectCallerId={handleCallerIdChange}
      onDeletePhone={handleDeletePhone}
      onAddNewNumber={() => navigateToStep("request")}
      onRefresh={handleRefresh}
      deleting={deleting}
      defaultPhoneNumber={defaultPhoneNumber}
      alreadyVerifiedMessage={alreadyVerifiedMessage}
      deleteSuccessMessage={deleteSuccessMessage}
    />
  );

  useEffect(() => {
    const updateCountry = async () => {
      const country = await detectCountry();
      setUserCountry(country);
    };
    updateCountry();
  }, []);

  // Render
  const renderContent = () => {
    switch (currentStep) {
      case "overview":
        return renderOverviewStep();
      case "request":
        return (
          <RequestStep
            phoneNumber={phoneNumber}
            onPhoneNumberChange={(formattedValue) =>
              setPhoneNumber(formattedValue)
            }
            onRequestVerification={handleRequestVerification}
            onCancel={() => navigateToStep("overview")}
            onExtensionClick={() => navigateToStep("extension")}
            onClearExtension={() => {
              setExtension(null);
              alert("info", "Extension cleared");
            }}
            requesting={requesting}
            extension={extension}
          />
        );
      case "extension":
        return (
          <ExtensionStep
            extension={extension}
            onCommit={(newExtension) => {
              setExtension(newExtension);
              alert("success", "Extension added successfully");
              navigateToStep("request");
            }}
            onCancel={() => {
              navigateToStep("request");
            }}
          />
        );
      case "verify":
        return (
          <VerifyStep
            validationCode={validationCode}
            onDone={handleDone}
            verificationProgress={verificationProgress}
            phoneNumber={phoneNumber}
          />
        );
      default:
        return renderOverviewStep();
    }
  };

  return (
    <div className="space-y-4">
      {renderContent()}
      <MessageDisplay error={error} success={success} />

      {currentStep === "overview" && (
        <div className="flex flex-row items-center justify-center">
          <DSButton
            variant="secondary"
            onClick={onClose}
            dataTestId="scribephone-back-to-dialer-button"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />{" "}
            Back to Dialer
          </DSButton>
        </div>
      )}
    </div>
  );
};

export default PhoneVerificationStep;
